/* eslint-disable @typescript-eslint/no-explicit-any */
import { CheckOutlined, ClearOutlined } from '@mui/icons-material'
import { Box, FormGroup, FormHelperText, Typography, BoxProps } from '@mui/material'
import _ from 'lodash'
import { Fragment, ReactNode, useEffect } from 'react'
import { ErrorOption, RegisterOptions, useForm } from 'react-hook-form'

import useAddressData from '../../hooks/useAddressData'
import { FormControl } from '../../views/Login/LoginForm/styles'
import CepField from '../CepField'
import CheckboxField from '../CheckboxField'
import CpfField from '../CpfField'
import DateField from '../DateField'
import InputPasswordField from '../InputPasswordField'
import InputTextField from '../InputTextField'
import PhoneField from '../PhoneField'

interface CustomRegisterOptions extends RegisterOptions {
  customValidate?: any
}

type ElementField = {
  id: string
  label: string
  rules?: CustomRegisterOptions
  type?: string
  disabled?: boolean
}

type ShowRules = {
  id: string
  title: string
  descriptions: any
}

type ElementGroup = {
  type: string
  fields: ElementField[]
  title?: string
  description?: string
}

type FormElement = {
  fields: ElementField[]
  group?: ElementGroup
  showRules?: ShowRules
}

interface IDynamicFormProps extends BoxProps {
  onSubmit: (data: any) => void
  formElements: FormElement[]
  formErrors?: any
  defaultValues?: any
  children?: ReactNode
}

function DynamicForm({ onSubmit, formElements, formErrors, defaultValues, children, ...props }: IDynamicFormProps) {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, dirtyFields },
    control,
    getValues,
    setValue,
    reset,
  } = useForm({ defaultValues, criteriaMode: 'all' })

  const { addressData, fetchAddressData } = useAddressData()

  useEffect(() => {
    Object.entries(formErrors || {}).forEach(([id, error], index) => {
      setError(id, error as ErrorOption, !index ? { shouldFocus: true } : { shouldFocus: false })
    })
  }, [setError, formErrors])

  useEffect(() => {
    if (!addressData) {
      return
    }
    const { zipCode, city, district, state, street, type } = addressData
    reset({
      cep: String(zipCode).padStart(8, '0'),
      city,
      neighborhood: district,
      state,
      street: `${type || ''} ${street || ''}`,
    })
  }, [addressData, reset])

  function customValidations(message: string, customValue?: any) {
    return {
      match: (value: any) => value === getValues(customValue) || message,
    }
  }

  function getField(id: string, label: string, rules = {} as CustomRegisterOptions, disabled?: boolean, type = '') {
    const { [id]: error } = errors
    const { customValidate, ...customRules } = rules

    if (customValidate) {
      customRules.validate = {
        ...customRules.validate,
        ..._.mapValues(customValidate, (value, key) => {
          if (typeof value === 'string') {
            return customValidations(value)[key as keyof typeof customValidations]
          }
          const { value: customValue, message } = value
          return customValidations(message, customValue)[key as keyof typeof customValidations]
        }),
      }
    }

    switch (type) {
      case 'cep':
        return (
          <CepField
            control={control}
            id={id}
            label={label}
            error={error}
            rules={customRules}
            loadAddress={fetchAddressData}
          />
        )
      case 'date':
        return <DateField control={control} id={id} label={label} error={error} rules={customRules} />
      case 'cpf':
        return (
          <CpfField
            control={control}
            id={id}
            label={label}
            disabled={disabled}
            error={error}
            setValue={setValue}
            {...register(id, {
              ...customRules,
              setValueAs: (value) => value.replace(/[^0-9]/g, ''),
            })}
          />
        )
      case 'phone':
        return (
          <PhoneField
            control={control}
            id={id}
            label={label}
            error={error}
            setValue={setValue}
            {...register(id, {
              ...customRules,
              setValueAs: (value) => value.replace(/[^0-9]/g, ''),
            })}
          />
        )
      case 'password':
        return <InputPasswordField id={id} label={label} error={error} {...register(id, customRules)} />
      default:
        return <InputTextField id={id} label={label} error={error} {...register(id, customRules)} />
    }
  }

  function getRuleValues(rule: any, isDirty: boolean) {
    if (rule) {
      return {
        icon: <ClearOutlined htmlColor="#fa0000" style={{ fontSize: '1.25rem' }} />,
        color: '#fa0000',
      }
    }
    if (!isDirty) {
      return {
        icon: <>-&nbsp;</>,
        color: '',
      }
    }
    return {
      icon: <CheckOutlined htmlColor="#10aa00" style={{ fontSize: '1.25rem' }} />,
      color: '#10aa00',
    }
  }

  function getRulesDescriptions(rules: any, error: any, isDirty: boolean): ReactNode {
    return Object.entries(rules).map(([key, value]) => {
      if (typeof value === 'string') {
        const { [key]: rule } = error?.types || {}
        const { icon, color } = getRuleValues(rule, isDirty)
        return (
          <Box key={`${key}description`} display="flex" alignItems="center">
            {icon}
            <Typography color={color} variant="caption">
              {value}
            </Typography>
          </Box>
        )
      }
      return getRulesDescriptions(value, error, isDirty)
    })
  }

  function getGroup(group: ElementGroup, index: number) {
    const { type, fields, title, description } = group
    const groupFields = () => {
      switch (type) {
        case 'checkbox':
          return (
            <FormGroup row>
              {fields.map(({ id, label }) => (
                <CheckboxField key={id} control={control} id={id} label={label} />
              ))}
            </FormGroup>
          )
        default:
          return null
      }
    }
    return (
      <Box key={`${type}${index}`} marginTop={2}>
        <Typography variant="subtitle1" fontWeight="bold">
          {title}
        </Typography>
        <Typography>{description}</Typography>
        {groupFields()}
      </Box>
    )
  }

  function getElement(fields: ElementField[], index: number, group?: ElementGroup, showRules?: ShowRules) {
    if (group) {
      return getGroup(group, index)
    }
    if (showRules) {
      const { [showRules.id]: error } = errors
      const { [showRules.id]: isDirty } = dirtyFields
      return (
        <Fragment key={showRules.id}>
          <FormHelperText style={{ fontSize: '0.938rem' }}>{showRules.title}</FormHelperText>
          {getRulesDescriptions(showRules.descriptions, error, isDirty)}
        </Fragment>
      )
    }
    return (
      <Box key={`element${index}`} display="flex" gap={1}>
        {fields.map(({ id, label, rules, disabled, type }) => (
          <FormControl key={id} fullWidth variant="outlined">
            {getField(id, label, rules, disabled, type)}
          </FormControl>
        ))}
      </Box>
    )
  }

  return (
    <Box
      marginTop={2}
      display="flex"
      component="form"
      autoComplete="off"
      flexDirection="column"
      onSubmit={handleSubmit(onSubmit)}
      {...props}
    >
      {formElements.map(({ fields, group, showRules }, index) => {
        return getElement(fields, index, group, showRules)
      })}
      {children}
    </Box>
  )
}

export default DynamicForm
